import ToastComponent from '~/components/ui/ToastComponent.vue';
import { useToast } from 'vue-toastification';
import { CSRF_COOKIE_KEY, CSRF_HEADER_KEY } from '~/constants/csrfKeys';
import type { UseFetchOptions } from 'nuxt/app';

type CustomFetchOptions<T> = UseFetchOptions<T> & {
  serverCacheKey?: string;
  serverCacheTtl?: number;
};

interface ServerFetchCacheItem {
  expiresAt: number;
  value: unknown;
}

const serverFetchCache = new Map<string, ServerFetchCacheItem>();

function stableStringify(value: unknown): string {
  if (!value || typeof value !== 'object') return String(value ?? '');
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;

  return `{${Object.entries(value)
    .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
    .map(([key, item]) => `${key}:${stableStringify(item)}`)
    .join(',')}}`;
}

function getServerCacheKey(url: string, baseURL: string, version: string, options: CustomFetchOptions<unknown>) {
  return options.serverCacheKey ?? `${baseURL}${version}:${url}:${stableStringify(options.query)}`;
}

export async function useCustomFetch<T>(
  url: string,
  options: CustomFetchOptions<T> = {},
  version: string = '1',
  skipError = false,
) {
  const authStore = useAuthStore();
  const { apiUrl } = useRuntimeConfig().public;
  const requestHeaders: any = useRequestHeaders(['cookie']);

  const csrfToken = useCookie(CSRF_COOKIE_KEY).value;

  if (csrfToken) {
    requestHeaders[CSRF_HEADER_KEY] = csrfToken;
  }

  requestHeaders.accept = 'application/json';

  const { serverCacheKey, serverCacheTtl, transform, getCachedData, key, ...fetchOptions } = options;
  const cacheKey = serverCacheTtl
    ? getServerCacheKey(url, apiUrl, version, options as CustomFetchOptions<unknown>)
    : undefined;

  const { data, error, status, execute } = await useFetch(url, {
    ...fetchOptions,
    $fetch: $fetch,
    baseURL: apiUrl + version,
    credentials: 'include',
    mode: 'cors',
    headers: requestHeaders,
    key: cacheKey ?? key,
    getCachedData:
      import.meta.server && cacheKey
        ? (key, nuxtApp, ctx) => {
            const cached = serverFetchCache.get(key);

            if (cached && Date.now() < cached.expiresAt) return cached.value as T;

            return getCachedData?.(key, nuxtApp, ctx);
          }
        : getCachedData,
    transform: cacheKey
      ? (input: T) => {
          const transformed = transform ? transform(input) : input;

          if (import.meta.server) {
            serverFetchCache.set(cacheKey, {
              expiresAt: Date.now() + (serverCacheTtl ?? 0),
              value: transformed,
            });
          }

          return transformed;
        }
      : transform,
  });

  if (error.value && error.value.statusCode) {
    console.error(error);

    if (
      authStore.accessRights?.info_panel &&
      import.meta.client &&
      [401, 403, 500, 502].includes(error.value.statusCode) &&
      error.value.data?.message
    ) {
      const toast = useToast();
      toast.error({ component: ToastComponent, props: { text: error.value.data.message } });
    }

    if (String(error.value.statusCode) === '404' && !skipError) {
      throw createError({ statusCode: 404, statusMessage: 'Page Not Found' });
    }
  }

  return { data, error, status, execute };
}
